/**
 * CSS Variable Color Helpers
 *
 * Utilities for working with MUI CSS variables in theme customization.
 * These helpers enable proper dark mode support by using CSS variables
 * when available, with graceful fallbacks to hex colors.
 *
 * Best Practices:
 * - Always use CSS variables for colors that change between light/dark modes
 * - Provide hex fallbacks for SSR and non-CSS-variable environments
 * - Use channel variables for alpha transparency
 *
 * @see https://mui.com/material-ui/customization/css-variables/
 */

import { alpha } from '@mui/material/styles';
import type { CssVarsTheme } from '@mui/material/styles';

/**
 * Build an `rgb(var(--channel) / opacity)` string for CSS variable alpha colors.
 * Falls back to alpha(hex, opacity) if channel variable is not available.
 *
 * @param _t - MUI CSS Variables theme (unused, kept for consistency)
 * @param channelVar - CSS variable channel (e.g., theme.vars.palette.primary.mainChannel)
 * @param hexFallback - Fallback hex color for non-CSS-variable environments
 * @param opacity - Alpha opacity value (0-1)
 * @returns CSS color string with alpha
 *
 * @example
 * ```tsx
 * alphaVar(theme, theme.vars?.palette?.primary?.mainChannel, "#00B2E3", 0.12)
 * // Returns: "rgb(var(--mui-palette-primary-mainChannel) / 0.12)"
 * // Or fallback: "rgba(0, 178, 227, 0.12)"
 * ```
 */
export function alphaVar(_t: CssVarsTheme, channelVar: string | undefined, hexFallback: string, opacity: number): string {
  if (channelVar) {
    // Use CSS variable with alpha channel
    return `rgb(${channelVar} / ${opacity})`;
  }
  // Fallback to MUI's alpha helper
  return alpha(hexFallback, opacity);
}

/**
 * Return a solid color from CSS variables (rgb(var(--channel) / 1)) with full opacity.
 * Falls back to hex color if channel variable is not available.
 *
 * @param _t - MUI CSS Variables theme (unused, kept for consistency)
 * @param channelVar - CSS variable channel (e.g., theme.vars.palette.primary.mainChannel)
 * @param hexFallback - Fallback hex color for non-CSS-variable environments
 * @returns CSS color string at full opacity
 *
 * @example
 * ```tsx
 * solidVar(theme, theme.vars?.palette?.primary?.mainChannel, "#00B2E3")
 * // Returns: "rgb(var(--mui-palette-primary-mainChannel) / 1)"
 * // Or fallback: "#00B2E3"
 * ```
 */
export function solidVar(_t: CssVarsTheme, channelVar: string | undefined, hexFallback: string): string {
  if (channelVar) {
    // Use CSS variable with full opacity
    return `rgb(${channelVar} / 1)`;
  }
  // Fallback to hex color
  return hexFallback;
}
